import React, { FC, forwardRef, MouseEvent, useCallback, useMemo } from 'react';
import { BaseEditor, createEditor, Descendant } from 'slate';
import { Editable, ReactEditor, RenderLeafProps, Slate, withReact } from 'slate-react';
import { RenderElementProps } from 'slate-react/dist/components/editable';

import styles from './editorViewer.module.scss';

declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

type BlockFormatType =
  | 'title'
  | 'sub-title'
  | 'paragraph'
  | 'bulleted-list'
  | 'numbered-list'
  | 'list-item'
  | 'image'
  | 'color-label'
  /**
   * NOTE: sprint/4-1 Text Size 재정의됨
   */
  | 't1'
  | 't2'
  | 't3'
  | 'embed';

export type CustomElement = {
  type: BlockFormatType;
  subType?: string;
  children: CustomElement[] | CustomText[];
  label?: string;
  url?: string;
  link?: string;
  size?: 'normal' | 'large';
};

export type MarkFormatType = 'bold' | 'underline' | 'highlight' | 'link';

export type CustomText = {
  text: string;
  bold?: boolean;
  highlight?: boolean;
  underline?: boolean;
  link?: string;
  label?: string;
};

type Props = {
  value: Descendant[];
  placeholder?: string;
};

// eslint-disable-next-line
const ImageEditorViewElement = forwardRef<
  HTMLDivElement,
  Omit<RenderElementProps, 'attributes'> & {
    isSelected?: boolean;
    handleImageClick?: (e: MouseEvent<HTMLImageElement>) => void;
    refToImage?: (node: HTMLImageElement) => void;
    editorViewMode?: boolean;
  }
>(({ children, element, isSelected, handleImageClick, editorViewMode, refToImage }, ref) => {
  return (
    <>
      <div
        className={styles['image-block']}
        onClick={(e) => {
          // e.preventDefault();
        }}
        ref={ref}
        contentEditable={false}
      >
        {element.link && !editorViewMode ? (
          <a href={element.link} target={'_blank'} rel="noreferrer">
            <img
              src={element.url}
              alt={element.label}
              style={{
                display: 'block',
                outline: isSelected ? '1px solid black' : 'none',
                outlineOffset: -1,
              }}
              onClick={(e) => {
                handleImageClick?.(e);
              }}
              ref={refToImage}
            />
          </a>
        ) : (
          <img
            src={element.url}
            alt={element.label}
            style={{ display: 'block', border: isSelected ? '1px solid #000' : 'none' }}
            onClick={(e) => {
              handleImageClick?.(e);
            }}
            ref={refToImage}
          />
        )}
      </div>
      <div style={{ display: 'none' }}>{children}</div>
    </>
  );
});

ImageEditorViewElement.displayName = 'ImageEditorViewElement';

const EditorViewer: FC<Props> = ({ value, placeholder }) => {
  const withColorLabel = (editor: ReactEditor) => {
    const { isInline, isVoid } = editor;

    editor.isInline = (element: CustomElement) => {
      return element.type === 'color-label' ? true : isInline(element);
    };

    editor.isVoid = (element: CustomElement) => {
      return element.type === 'color-label' ? true : isVoid(element);
    };

    return editor;
  };

  const withImages = (editor: ReactEditor) => {
    const { isVoid } = editor;
    editor.isVoid = (element) => (element.type === 'image' ? true : isVoid(element));
    return editor;
  };

  const editor = useMemo(() => withImages(withColorLabel(withReact(createEditor()))), []);
  const renderElement = useCallback((props: RenderElementProps) => <Element {...props} />, []);
  const renderLeaf = useCallback((props: RenderLeafProps) => <Leaf {...props} />, []);

  return (
    <Slate
      editor={editor}
      value={value}
      onChange={() => {
        //
      }}
    >
      <Editable
        renderPlaceholder={({ children, attributes }) => (
          <span {...attributes} className={styles['placeholder']}>
            {children}
          </span>
        )}
        placeholder={placeholder}
        className={styles['editor']}
        renderElement={renderElement}
        renderLeaf={renderLeaf}
        readOnly
      />
    </Slate>
  );
};

const Element = ({ attributes, children, element }: RenderElementProps) => {
  switch (element.type) {
    /************ deprecated ************/
    case 'title':
      return <h1 {...attributes}>{children}</h1>;
    case 'sub-title':
      return <h2 {...attributes}>{children}</h2>;
    /************************************/
    case 'bulleted-list':
      return <ul {...attributes}>{children}</ul>;
    case 'numbered-list':
      return <ol {...attributes}>{children}</ol>;
    case 'list-item':
      return (
        <li {...attributes}>
          <div>{children}</div>
        </li>
      );
    case 'image':
      return (
        <div {...attributes} className={styles['image-block']}>
          <ImageEditorViewElement element={element}>{children}</ImageEditorViewElement>
        </div>
        // <div {...attributes} contentEditable={false}>
        //   <div className={cx('image-block')}>
        //     <img src={element.url} alt={element.label} style={{ display: 'block' }} />
        //     <div hidden>{children}</div>
        //   </div>
        // </div>
      );
    case 'color-label':
      return element.size === 'large' ? (
        <h2 className={styles['t2']} {...attributes}>
          {element.label}
          {children}
        </h2>
      ) : (
        <span className={styles['color-label']} {...attributes}>
          {element.label}
          {children}
        </span>
      );
    case 't1':
      return (
        <h1 className={styles['t1']} {...attributes}>
          {children}
        </h1>
      );
    case 't2':
      return (
        <h2 className={styles['t2']} {...attributes}>
          {children}
        </h2>
      );
    case 't3':
      return (
        <p className={styles['t3']} {...attributes}>
          {children}
        </p>
      );
    case 'embed':
      return (
        <div className={styles['embed']} {...attributes}>
          <iframe
            width="560"
            height="315"
            src={element.url}
            frameBorder="0"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
          />
          {children}
        </div>
      );
    default:
      return <p {...attributes}>{children}</p>;
  }
};

const Leaf = ({ attributes, children, leaf }: RenderLeafProps) => {
  if (leaf.bold) {
    children = <b>{children}</b>;
  }

  if (leaf.highlight) {
    children = <strong>{children}</strong>;
  }

  if (leaf.underline) {
    children = <u>{children}</u>;
  }

  if (leaf.link) {
    children = <a onClick={() => window.open(leaf.link, '_blank')}>{children}</a>;
  }

  return <span {...attributes}>{children}</span>;
};

export default EditorViewer;
